import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Clock, DollarSign, User } from 'lucide-react';
import { format } from 'date-fns';

const RideRequestBoard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/ride-requests`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load ride requests');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/claim`);
      toast.success('Ride claimed successfully!');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim ride');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading ride requests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ride-request-board-page">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Ride Request Board</h2>
        <p className="text-gray-600 mt-1">Riders need drivers - claim a ride and earn 80%</p>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center py-8 text-gray-500">No ride requests at the moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {requests.map((request) => (
            <Card key={request.id} className="hover:shadow-md transition-shadow" data-testid={`ride-request-${request.id}`}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        {request.client_name}
                      </h3>
                      {request.client_phone && (
                        <p className="text-sm text-gray-600">{request.client_phone}</p>
                      )}
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">Ride Request</Badge>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{format(new Date(request.date), 'EEE, MMM dd, yyyy - h:mm a')}</span>
                    </div>
                    
                    <div className="text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <strong>Pickup:</strong> {request.pickup_location}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4 opacity-0" />
                        <strong>Dropoff:</strong> {request.dropoff_location}
                      </div>
                    </div>
                    
                    <div className="flex gap-4 text-gray-600">
                      <span>{request.miles_driven} miles</span>
                      <span>•</span>
                      <span>{(request.time_hours * 60).toFixed(0)} minutes</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <div>
                      <div className="text-sm text-gray-600">Total Fare</div>
                      <div className="text-2xl font-bold">${request.total_amount.toFixed(2)}</div>
                      <div className="text-sm text-green-600 font-semibold">
                        You earn: ${request.net_earnings.toFixed(2)} (80%)
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => handleClaim(request.id)}
                      data-testid={`claim-request-${request.id}`}
                    >
                      Claim Ride
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RideRequestBoard;
